import { Ellipsis, Mail, MessageSquare, Phone } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { type TicketDto, type TicketFeedbackMethod, type TicketResolutionInput, TicketStatus } from "@/api/tickets";
import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import {
  MAX_FEEDBACK_METHOD_OTHER_LENGTH,
  MAX_RESOLUTION_RESULT_LENGTH,
  prepareTicketResolution,
  TICKET_FEEDBACK_METHODS,
  type TicketResolutionErrors,
} from "../model/ticket-resolution";

type TicketResolutionDialogProps = {
  ticket: TicketDto | null;
  isPending: boolean;
  errorMessage: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: TicketResolutionInput) => void;
  onUnsavedWorkChange?: (hasUnsavedWork: boolean) => void;
};

const feedbackMethodIcons = { phone: Phone, sms: MessageSquare, email: Mail, other: Ellipsis };

/** 未打开时卸载表单；同一次打开中的服务端错误与并发刷新保留输入。 */
export function TicketResolutionDialog(props: TicketResolutionDialogProps) {
  return props.ticket ? <TicketResolutionForm key={props.ticket.id} {...props} ticket={props.ticket} /> : null;
}

function TicketResolutionForm({
  ticket,
  isPending,
  errorMessage,
  onOpenChange,
  onSubmit,
  onUnsavedWorkChange,
}: TicketResolutionDialogProps & { ticket: TicketDto }) {
  const { t } = useI18n();
  const formId = useId();
  const resultRef = useRef<HTMLTextAreaElement>(null);
  const methodRef = useRef<HTMLButtonElement>(null);
  const otherRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState("");
  const [method, setMethod] = useState<TicketFeedbackMethod | "">("");
  const [other, setOther] = useState("");
  const [errors, setErrors] = useState<TicketResolutionErrors>({});
  const hasUnsavedWork = isPending || result.length > 0 || method !== "" || other.length > 0;
  const canResolve = ticket.status === TicketStatus.Open;
  const submitError = canResolve ? errorMessage : t("tickets.resolution.statusChangedError");

  useEffect(() => {
    onUnsavedWorkChange?.(hasUnsavedWork);
  }, [hasUnsavedWork, onUnsavedWorkChange]);

  useEffect(() => () => onUnsavedWorkChange?.(false), [onUnsavedWorkChange]);

  const handleOpenChange = (open: boolean) => {
    if (!isPending) onOpenChange(open);
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-full overflow-y-auto sm:max-w-lg" showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>{t("tickets.resolution.dialogTitle")}</DialogTitle>
          <DialogDescription>{t("tickets.resolution.dialogDescription", { number: ticket.number })}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (isPending || !canResolve) return;
            const { errors: nextErrors, input } = prepareTicketResolution({ result, method, other });
            setErrors(nextErrors);
            if (nextErrors.result) resultRef.current?.focus();
            else if (nextErrors.method) methodRef.current?.focus();
            else if (nextErrors.other) otherRef.current?.focus();
            else if (input) onSubmit(input);
          }}
        >
          <FieldGroup>
            <Field data-invalid={Boolean(errors.result) || undefined}>
              <FieldLabel htmlFor={`${formId}-result`}>{t("tickets.resolution.resultLabel")}</FieldLabel>
              <Textarea
                ref={resultRef}
                aria-describedby={errors.result ? `${formId}-result-error` : undefined}
                aria-invalid={Boolean(errors.result)}
                autoFocus
                disabled={isPending}
                id={`${formId}-result`}
                maxLength={MAX_RESOLUTION_RESULT_LENGTH}
                placeholder={t("tickets.resolution.resultPlaceholder")}
                required
                rows={5}
                value={result}
                onChange={(event) => {
                  setResult(event.target.value);
                  setErrors((current) => ({ ...current, result: undefined }));
                }}
              />
              {errors.result ? <FieldError id={`${formId}-result-error`}>{t(errors.result)}</FieldError> : null}
            </Field>
            <Field data-invalid={Boolean(errors.method) || undefined}>
              <FieldLabel id={`${formId}-method-label`}>{t("tickets.resolution.feedbackMethodLabel")}</FieldLabel>
              <div className="overflow-hidden rounded-lg border bg-background">
                <RadioGroup
                  aria-describedby={errors.method ? `${formId}-method-error` : undefined}
                  aria-invalid={Boolean(errors.method)}
                  aria-labelledby={`${formId}-method-label`}
                  className="grid-cols-1 gap-0 divide-y divide-border"
                  disabled={isPending}
                  required
                  value={method}
                  onValueChange={(value) => {
                    setMethod(value as TicketFeedbackMethod);
                    setErrors((current) => ({ ...current, method: undefined, other: undefined }));
                  }}
                >
                  {TICKET_FEEDBACK_METHODS.map((value, index) => {
                    const MethodIcon = feedbackMethodIcons[value];
                    const selected = method === value;
                    return (
                      <Label
                        className={cn(
                          "min-h-12 w-full cursor-pointer gap-3 px-3 py-2 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-ring",
                          selected ? "bg-primary/5" : "hover:bg-muted/50",
                          isPending && "pointer-events-none opacity-60",
                        )}
                        htmlFor={`${formId}-method-${value}`}
                        key={value}
                      >
                        <span
                          className={cn(
                            "flex size-8 shrink-0 items-center justify-center rounded-md transition-colors",
                            selected ? "bg-primary/10 text-primary" : "bg-muted/60 text-muted-foreground",
                          )}
                        >
                          <MethodIcon aria-hidden="true" className="size-4" />
                        </span>
                        <span className="flex-1 leading-5">{t(`tickets.resolution.method.${value}`)}</span>
                        <RadioGroupItem className="after:inset-0" id={`${formId}-method-${value}`} ref={index === 0 ? methodRef : undefined} value={value} />
                      </Label>
                    );
                  })}
                </RadioGroup>
                {method === "other" ? (
                  <Field className="bg-primary/5 pr-3 pb-3 pl-14" data-invalid={Boolean(errors.other) || undefined}>
                    <FieldLabel className="text-xs text-muted-foreground" htmlFor={`${formId}-other`}>
                      {t("tickets.resolution.otherLabel")}
                    </FieldLabel>
                    <Input
                      ref={otherRef}
                      aria-describedby={errors.other ? `${formId}-other-error` : undefined}
                      aria-invalid={Boolean(errors.other)}
                      disabled={isPending}
                      id={`${formId}-other`}
                      maxLength={MAX_FEEDBACK_METHOD_OTHER_LENGTH}
                      placeholder={t("tickets.resolution.otherPlaceholder")}
                      required
                      value={other}
                      onChange={(event) => {
                        setOther(event.target.value);
                        setErrors((current) => ({ ...current, other: undefined }));
                      }}
                    />
                    {errors.other ? <FieldError id={`${formId}-other-error`}>{t(errors.other)}</FieldError> : null}
                  </Field>
                ) : null}
              </div>
              {errors.method ? <FieldError id={`${formId}-method-error`}>{t(errors.method)}</FieldError> : null}
            </Field>
            {submitError ? <FieldError role="alert">{submitError}</FieldError> : null}
          </FieldGroup>
          <DialogFooter className="mt-6">
            <Button disabled={isPending} type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t("tickets.actions.cancel")}
            </Button>
            <Button disabled={isPending || !canResolve} type="submit">
              {isPending ? <Loader className="text-current" label={t("tickets.resolution.submitting")} size={16} /> : null}
              {t(isPending ? "tickets.resolution.submitting" : "tickets.resolution.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
